/** Keys for pause/resume button loading state (shared by upload popup + indicator). */
export const uploadControlLoadingKey = {
  folderPause: (folderName: string) => `folder:pause:${folderName}`,
  folderResume: (folderName: string) => `folder:resume:${folderName}`,
  filePause: (fileId: string) => `file:pause:${fileId}`,
  fileResume: (fileId: string) => `file:resume:${fileId}`,
};

export interface UploadControlActionOptions {
  minVisibleMs?: number;
  pollIntervalMs?: number;
  timeoutMs?: number;
}

/**
 * Shows a short loader until the upload state settles (pause/resume can lag behind the click).
 */
export async function runUploadControlAction(
  loadingKeys: Set<string>,
  key: string,
  onChange: () => void,
  run: () => void,
  isSettled: () => boolean,
  options?: UploadControlActionOptions
): Promise<void> {
  if (loadingKeys.has(key)) {
    return;
  }

  const minVisibleMs = options?.minVisibleMs ?? 400;
  const pollIntervalMs = options?.pollIntervalMs ?? 80;
  const timeoutMs = options?.timeoutMs ?? 12000;

  loadingKeys.add(key);
  onChange();

  const started = Date.now();
  try {
    run();
    while (!isSettled()) {
      await delay(pollIntervalMs);
      onChange();
      if (Date.now() - started > timeoutMs) {
        break;
      }
    }
  } finally {
    const remain = minVisibleMs - (Date.now() - started);
    if (remain > 0) {
      await delay(remain);
    }
    loadingKeys.delete(key);
    onChange();
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
